import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { useTheme } from '../../../contexts/ThemeContext';
import DarkModeWrapper from '../../../components/DarkModeWrapper';

const RecentHighlights = () => {
  const [activeProject, setActiveProject] = useState(0);
  const { isDark } = useTheme();

  const recentProjects = [
    {
      id: 1,
      title: 'E-Commerce Analytics Dashboard',
      description: `A comprehensive analytics platform built with React and Node.js that provides real-time insights for e-commerce businesses.\n\nFeatures include sales tracking, customer behavior analysis, inventory management, and predictive analytics using machine learning algorithms.`,
      image: '/assets/Projects/E-Commerce Analytics Dashboard.png',
      technologies: ['React', 'Node.js', 'MongoDB', 'Chart.js', 'AWS'],
      status: 'Live',
      metrics: {
        users: '2.5K+',
        performance: '98%',
        uptime: '99.9%'
      },
      demoUrl: '#',
      githubUrl: '#',
      featured: true
    },
    {
      id: 2,
      title: 'AI-Powered Task Management',
      description: `Smart task management application that uses AI to prioritize tasks, predict completion times, and optimize team productivity.\n\nIntegrates with popular tools like Slack, Google Calendar, and GitHub to provide seamless workflow automation.`,
      image: '/assets/Projects/AI-Powered Task Management.png',
      technologies: ['React', 'Python', 'TensorFlow', 'PostgreSQL', 'Docker'],
      status: 'In Development',
      metrics: {
        accuracy: '94%',
        efficiency: '+40%',
        adoption: '85%'
      },
      demoUrl: '#',
      githubUrl: '#',
      featured: false
    },
    {
      id: 3,
      title: 'Real-time Collaboration Platform',
      description: `Multi-user collaboration platform with real-time editing, video conferencing, and project management capabilities.\n\nBuilt with modern web technologies to support distributed teams and enhance remote work productivity.`,
      image: '/assets/Projects/Real-time Collaboration Platform.png',
      technologies: ['Next.js', 'Socket.io', 'WebRTC', 'Redis', 'Kubernetes'],
      status: 'Beta',
      metrics: {
        latency: '<50ms',
        concurrent: '1K+',
        satisfaction: '4.8/5'
      },
      demoUrl: '#',
      githubUrl: '#',
      featured: false
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <DarkModeWrapper sectionName="recent-highlights">
      <section className="py-20">
        <div className="container mx-auto px-6 lg:px-8">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-text-primary mb-4">
              Recent Highlights
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Latest projects showcasing innovative solutions and cutting-edge technologies
            </p>
          </motion.div>

          <div className="max-w-7xl mx-auto">
            {/* Featured Project */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-16"
            >
              <div className={`${isDark ? 'bg-gradient-to-br from-primary/10 to-brand-purple/10 border-primary/30' : 'bg-gradient-to-br from-primary/5 to-brand-purple/5 border-primary/10'} rounded-2xl p-8 border`}>
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                  {/* Project Info */}
                  <div className="space-y-6">
                    <div className="flex items-center space-x-3">
                      <span className="px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full">
                        Featured Project
                      </span>
                      <span className="px-3 py-1 bg-success/10 text-success text-sm font-medium rounded-full">
                        {recentProjects[0].status}
                      </span>
                    </div>

                    <h3 className="text-3xl font-bold text-text-primary">
                      {recentProjects[0].title}
                    </h3>

                    <p className="text-text-secondary leading-relaxed whitespace-pre-line">
                      {recentProjects[0].description}
                    </p>

                    {/* Technologies */}
                    <div className="flex flex-wrap gap-2">
                      {recentProjects[0].technologies.map((tech, index) => (
                        <span
                          key={index}
                          className={`px-3 py-1 rounded-lg text-sm font-medium ${isDark ? 'bg-slate-800 border-gray-700 text-slate-300' : 'bg-white border-border text-text-secondary'} border`}
                        >
                          {tech}
                        </span>
                      ))}
                    </div>

                    {/* Metrics */}
                    <div className="grid grid-cols-3 gap-4">
                      {Object.entries(recentProjects[0].metrics).map(([key, value], index) => (
                        <div key={index} className="text-center">
                          <p className="text-2xl font-bold text-primary">{value}</p>
                          <p className="text-sm text-text-secondary capitalize">{key}</p>
                        </div>
                      ))}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Button
                        variant="default"
                        iconName="ExternalLink"
                        iconPosition="right"
                        className="hover-lift"
                      >
                        View Live Demo
                      </Button>
                      <Button
                        variant="outline"
                        iconName="Github"
                        iconPosition="left"
                        className="hover-lift"
                      >
                        View Code
                      </Button>
                    </div>
                  </div>

                  {/* Project Image */}
                  <div className="relative">
                    <div className="relative rounded-xl overflow-hidden shadow-premium">
                      <Image
                        src={recentProjects[0].image}
                        alt={recentProjects[0].title}
                        className="w-full h-80 object-cover hover-scale transition-premium"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                    </div>

                    {/* Floating Stats */}
                    <motion.div
                      animate={{ y: [0, -10, 0] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className={`absolute -top-4 -right-4 rounded-lg p-4 shadow-premium border ${isDark ? 'bg-slate-800 border-gray-700' : 'bg-white border-border'}`}
                    >
                      <div className="flex items-center space-x-2">
                        <Icon name="TrendingUp" size={20} className="text-success" />
                        <span className="text-sm font-medium text-text-primary">Live & Growing</span>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Other Projects Grid */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid md:grid-cols-2 gap-8 mb-12"
            >
              {recentProjects.slice(1).map((project, index) => (
                <motion.div
                  key={project.id}
                  variants={itemVariants}
                  className={`rounded-xl border shadow-sm hover:shadow-premium transition-premium group ${isDark ? 'border-gray-700' : 'border-border'}`}
                >
                  {/* Project Image */}
                  <div className="relative rounded-t-xl overflow-hidden">
                    <Image
                      src={project.image}
                      alt={project.title}
                      className="w-full h-48 object-cover group-hover:scale-105 transition-premium"
                    />
                    <div className="absolute top-4 left-4">
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                        project.status === 'Live' ?'bg-success/10 text-success' 
                          : project.status === 'Beta' ?'bg-warning/10 text-warning' :'bg-primary/10 text-primary'
                      }`}>
                        {project.status}
                      </span>
                    </div>
                  </div>

                  {/* Project Content */}
                  <div className="p-6 space-y-4">
                    <h4 className="text-xl font-semibold text-text-primary group-hover:text-primary transition-premium">
                      {project.title}
                    </h4>

                    <p className="text-text-secondary text-sm leading-relaxed line-clamp-3">
                      {project.description.split('\n')[0]}
                    </p>

                    {/* Technologies */}
                    <div className="flex flex-wrap gap-2">
                      {project.technologies.slice(0, 3).map((tech, techIndex) => (
                        <span
                          key={techIndex}
                          className={`px-2 py-1 text-xs rounded ${isDark ? 'bg-slate-800 text-slate-300' : 'bg-surface text-text-secondary'}`}
                        >
                          {tech}
                        </span>
                      ))}
                      {project.technologies.length > 3 && (
                        <span className={`px-2 py-1 text-xs rounded ${isDark ? 'bg-slate-800 text-slate-300' : 'bg-surface text-text-secondary'}`}>
                          +{project.technologies.length - 3} more
                        </span>
                      )}
                    </div>

                    {/* Action Link */}
                    <div className="flex justify-end">
                      <Link
                        to="#"
                        className={`inline-flex items-center text-sm font-medium ${isDark ? 'text-primary hover:text-primary-light' : 'text-primary hover:text-primary/80'} transition-premium`}
                      >
                        <span>View Project</span>
                        <Icon name="ArrowRight" size={16} className="ml-1" />
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* View All Projects CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-center"
            >
              <Link to="/projects-innovation-laboratory">
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Grid"
                  iconPosition="left"
                  className="hover-lift"
                >
                  View All Projects
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </DarkModeWrapper>
  );
};

export default RecentHighlights;